import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { Button, NumberInput } from "@mantine/core";
import { SegmentedControl } from "@mantine/core";
import useAccount from "@/hooks/useAccount";
import useRequest from "@/hooks/useRequest";
import { numberWithCommas, katAmountDesc } from "@/utils/format";
import BigNumber from "bignumber.js";
import useStakeContract from "@/hooks/staking/useStakeContract";
import { ArrowDownIcon } from "@/components/common/icons";
import { useDisclosure } from "@mantine/hooks";
import UnStakeList from "@/components/network/staking/UnStakeList";
import type { UserStakeInfo } from "@/types/network";
import { toast } from "react-toastify";
import useUserStakingRewards from "@/hooks/staking/useUserStakingRewards";
import useRewardContract from "@/hooks/staking/useRewardContract";
import StakeRulesDialog from "@/components/network/staking/StakeRulesDialog";

const UserStakeCard: FunctionComponent<{
  tokenId: number | string;
  className?: string;
}> = function ({ className, tokenId }) {
  const [focus, setFocus] = useState(false);
  const [invested, setInvested] = useState(true);
  const [investValue, setInvestValue] = useState<number | undefined>(undefined);
  const [investError, setInvestError] = useState("");
  const [tab, setTab] = useState<"stake" | "unstake" | "claim">("stake");
  const { address, login, balance, setBalance } = useAccount();
  const { stake, staking, unStakeBatch, unStaking } = useStakeContract(
    String(tokenId)
  );
  const [unStakeAmount, setUnStakeAmount] = useState(BigNumber(0));
  const [tokenIds, setTokenIds] = useState<string[]>([]);
  const [availableTokenIds, setAvailableTokenIds] = useState<string[]>([]);
  const [unStakeRecordShowed, { toggle }] = useDisclosure(false);
  const { refetch: refreshRewards, rewards } = useUserStakingRewards(tokenIds);
  const [earningAmount, setEarningAmount] = useState(new BigNumber(0));

  const { claimStakerBatch, claiming } = useRewardContract();
  const [
    isStakeRuleDialogOpen,
    { open: openStakeRuleDialog, close: closeStakeRuleDialog },
  ] = useDisclosure(false);

  useEffect(() => {
    const earningAmount = rewards.reduce((total, val) => {
      if (String(val).length > 36) {
        return total.plus(new BigNumber(String(val)).div(1e18).div(1e18));
      } else {
        return total.plus(new BigNumber(String(val)).div(1e18));
      }
    }, new BigNumber(0));
    setEarningAmount(earningAmount);
  }, [rewards]);

  const { data: userStakeInfo, mutate } = useRequest<UserStakeInfo>(
    address && typeof tokenId !== "undefined"
      ? {
          function: "stake/getUserStakeInfo",
          data: {
            address,
            validatorId: String(tokenId),
          },
        }
      : null
  );

  const onStake = useCallback(async () => {
    if (investValue) {
      const stakeRes = await stake(investValue);
      if (stakeRes === true) {
        mutate(
          userStakeInfo
            ? {
                ...userStakeInfo,
                stakedAmount: new BigNumber(userStakeInfo.stakedAmount)
                  .plus(investValue)
                  .toString(),
              }
            : undefined,
          {
            revalidate: false,
          }
        );
        setBalance(balance.minus(investValue));
      }
    }
  }, [investValue, stake, mutate, userStakeInfo, setBalance, balance]);

  const onBeforeStake = useCallback(() => {
    if (investValue) {
      openStakeRuleDialog();
    }
  }, [investValue, openStakeRuleDialog]);

  const onUnStake = useCallback(async () => {
    if (earningAmount.isGreaterThan(0)) {
      setTab("claim");
      toast.info("You must claim earnings before Un-stake");
      return;
    }
    if (availableTokenIds.length > 0) {
      const unStakeRes = await unStakeBatch(availableTokenIds.slice(0, 10));
      if (unStakeRes === true) {
        mutate(
          userStakeInfo
            ? {
                stakedAmount: new BigNumber(userStakeInfo.stakedAmount)
                  .minus(unStakeAmount)
                  .toString(),
                stakeList: userStakeInfo.stakeList.filter((item) => {
                  return !availableTokenIds.slice(0, 10).includes(item.tokenId);
                }),
              }
            : undefined,
          {
            revalidate: false,
          }
        );
        setBalance(balance.plus(unStakeAmount.multipliedBy(0.95)));
      }
    }
  }, [
    unStakeBatch,
    mutate,
    availableTokenIds,
    earningAmount,
    userStakeInfo,
    unStakeAmount,
    setBalance,
    balance,
  ]);

  const onClaim = useCallback(async () => {
    let idList = [] as string[];
    rewards.forEach((val, index) => {
      if (val !== 0n) {
        idList.push(tokenIds[index]);
      }
    });
    if (idList.length > 0) {
      const claimRes = await claimStakerBatch(idList.slice(0, 10));
      if (claimRes === true) {
        setEarningAmount(new BigNumber(0));
        setBalance(balance.plus(earningAmount.multipliedBy(0.95)));
      }
    }
  }, [
    claimStakerBatch,
    tokenIds,
    refreshRewards,
    rewards,
    setEarningAmount,
    setBalance,
    balance,
    earningAmount,
  ]);

  const stakedAmount = numberWithCommas(
    new BigNumber(userStakeInfo?.stakedAmount || "0").toNumber()
  );

  useEffect(() => {
    let unStakeAmount = new BigNumber(0);
    const availableTokenIds: string[] = [];
    const tokenIds: string[] = [];
    userStakeInfo?.stakeList?.forEach((item) => {
      if (item.unLockTime <= Date.now()) {
        unStakeAmount = unStakeAmount.plus(new BigNumber(item.stakedAmount));
        availableTokenIds.push(item.tokenId);
      }
      tokenIds.push(item.tokenId);
    });
    setUnStakeAmount(unStakeAmount);
    setAvailableTokenIds(availableTokenIds);
    setTokenIds(tokenIds);
  }, [userStakeInfo]);

  const unStakeAmountDesc = numberWithCommas(unStakeAmount.toNumber());

  return (
    <div
      className={`mt-6 bg-[#EBF3FF] relative rounded-lg w-full p-6 ${
        className || ""
      }`}
    >
      {!address && (
        <div className="absolute w-full h-full z-10 flex items-center justify-center left-0 top-0">
          <Button radius="xl" size="md" onClick={login}>
            Connect wallet
          </Button>
        </div>
      )}
      <div className={`${address ? "" : "blur-sm"}`}>
        {invested && (
          <SegmentedControl
            className="w-full mb-6"
            radius="md"
            classNames={{
              indicator: "border border-primary",
              control: "!border-none ",
              root: "bg-[#F9F9F9]",
            }}
            size="md"
            value={tab}
            onChange={setTab as any}
            data={[
              { label: "Stake", value: "stake" },
              { label: "Un-stake", value: "unstake" },
              { label: "Claim", value: "claim" },
            ]}
          />
        )}

        {tab === "stake" && (
          <>
            <div className="flex items-center">
              <div className="flex flex-col items-center flex-1">
                <div className="font-bold text-xl text-tertiary">
                  {katAmountDesc(Number(balance), 2)} KAT
                </div>
                <div className="text-sm">KAT balance</div>
              </div>
              <div className="bg-[#87B5FF] w-[1px] h-10"></div>
              <div className="flex flex-col items-center flex-1">
                <div className="font-bold text-xl text-tertiary">
                  {stakedAmount} KAT
                </div>
                <div className="text-sm">Staked amount</div>
              </div>
            </div>
            <div
              className={`mt-6 !mb-0 flex items-center  px-3 border  border-solid  rounded ${
                investError
                  ? "input-error-block !bg-[rgba(250,82,82,0.10)] !border-error"
                  : ""
              } ${focus ? "form-focus-block" : ""} bg-white pt-2 pb-1`}
            >
              <div className="flex-1">
                <div
                  className={`text-dark1 text-bodyS font-semibold ${
                    investError ? "!text-error" : ""
                  }`}
                >
                  Staking amount
                </div>
                <div className="flex items-center">
                  <div className="mr-1 text-sm mt-[2px]">$</div>
                  <NumberInput
                    classNames={{
                      input: "!text-body !border-none !p-0 !bg-transparent",
                      wrapper: "!mb-0",
                    }}
                    hideControls
                    thousandsSeparator=","
                    step={1}
                    value={investValue}
                    onChange={(value) => {
                      setInvestValue(value as any);
                      setInvestError("");
                    }}
                    size="xs"
                    autoComplete={"off"}
                    spellCheck={false}
                    onFocus={() => {
                      setFocus(true);
                    }}
                    onBlur={() => {
                      setFocus(false);
                    }}
                  />
                </div>
              </div>
              <div className="text-xs font-bold text-gray1 text-right">KAT</div>
            </div>

            <Button
              className="h-[56px] w-full mt-6"
              radius="xl"
              loading={staking}
              onClick={onBeforeStake}
              disabled={process.env.NEXT_PUBLIC_DISABLE_STAKING === "true"}
            >
              Stake
            </Button>
            {process.env.NEXT_PUBLIC_DISABLE_STAKING === "true" && (
              <div className="mt-2 text-xs text-center max-w-[300px] mx-auto text-dark2">
                stake is temporarily unavailable while the contract upgrading
              </div>
            )}
          </>
        )}

        {tab === "unstake" && (
          <>
            <div className="flex items-center">
              <div className="flex flex-col items-center flex-1">
                <div className="font-bold text-xl text-tertiary">
                  {unStakeAmountDesc} KAT
                </div>
                <div
                  className="text-sm flex items-center action select-none"
                  onClick={toggle}
                >
                  <span>Available to un-stake</span>
                  <ArrowDownIcon
                    className={`w-2 ml-2 mt-1 transition-all origin-center ${
                      unStakeRecordShowed ? "rotate-180" : ""
                    }`}
                  ></ArrowDownIcon>
                </div>
              </div>
            </div>

            {unStakeRecordShowed && (
              <div className="mt-6">
                <UnStakeList
                  items={userStakeInfo?.stakeList || []}
                ></UnStakeList>
              </div>
            )}

            <Button
              className="h-[56px] w-full mt-6"
              radius="xl"
              loading={unStaking}
              onClick={onUnStake}
              disabled={
                process.env.NEXT_PUBLIC_DISABLE_STAKING === "true" ||
                unStakeAmount.isEqualTo(0)
              }
            >
              Un-stake
            </Button>
            {process.env.NEXT_PUBLIC_DISABLE_STAKING === "true" ? (
              <div className="mt-2 text-xs text-center max-w-[300px] mx-auto text-dark2">
                unstake is temporarily unavailable while the contract upgrading
              </div>
            ) : (
              <div className="mt-2 text-xs text-center max-w-[300px] mx-auto text-dark2">
                When you unstake, a 5% fee in KAT tokens will be automatically
                burned.
              </div>
            )}
          </>
        )}

        {tab === "claim" && (
          <>
            <div className="flex items-center">
              <div className="flex flex-col items-center flex-1">
                <div className="font-bold text-xl text-tertiary break-all text-center">
                  {katAmountDesc(earningAmount.toNumber(), 2)} KAT
                </div>
                <div className="text-sm">Total earnings</div>
              </div>
            </div>

            <Button
              className="h-[56px] w-full mt-6"
              radius="xl"
              disabled={
                process.env.NEXT_PUBLIC_DISABLE_STAKING === "true" ||
                earningAmount.isEqualTo(0)
              }
              loading={claiming}
              onClick={onClaim}
            >
              Claim
            </Button>
            {process.env.NEXT_PUBLIC_DISABLE_STAKING === "true" ? (
              <div className="mt-2 text-xs text-center max-w-[300px] mx-auto text-dark2">
                Claim is temporarily unavailable while the contract upgrading
              </div>
            ) : (
              <div className="mt-2 text-xs text-center max-w-[300px] mx-auto text-dark2">
                When you claim, a 5% fee in KAT tokens will be automatically
                burned.
              </div>
            )}
          </>
        )}
      </div>
      <StakeRulesDialog
        opened={isStakeRuleDialogOpen}
        onClose={closeStakeRuleDialog}
        onAgree={onStake}
      ></StakeRulesDialog>
    </div>
  );
};

export default UserStakeCard;
